// Keyboard handlers for the "Custom plans" section (#4341, #5136). The
// section owns ONE document-typed `onKeyDown` on its list wrapper, NOT through
// `lib/hotkeys.ts` — that module binds ONE document listener behind a closed
// `{onPalette,onShortcuts}` type from a single call site (`AppShell.tsx`),
// and widening it (or adding a second document listener) for one route's
// search box would damage an invariant this section does not need.
// `isTypingTarget` is reused so `/` typed into the search input inserts a
// slash instead of re-focusing it, and every handled key is `preventDefault()`
// — Firefox's quick-find is bound to bare `/`.
//
// The search-box DEBOUNCE seam lives in `lib/debounced-search.ts`, not here —
// this module is keyboard handling only.

import { isTypingTarget } from '../lib/hotkeys';

/** Every `.custom-plan-row` button inside the list wrapper, in DOM order. */
export function customPlanRowButtons(
  listWrap: HTMLDivElement | undefined,
): readonly HTMLButtonElement[] {
  if (listWrap === undefined) return [];
  return Array.from(listWrap.querySelectorAll<HTMLButtonElement>('.custom-plan-row'));
}

/**
 * Key handler options — the section's own closure (refs, toggles) is passed
 * IN, not captured. The function is pure of module scope: a future move of
 * the section does not have to drag every caller-state with it.
 */
export type CustomPlanListKeyDownOpts = {
  readonly event: KeyboardEvent;
  readonly listWrap: HTMLDivElement | undefined;
  readonly searchInput: HTMLInputElement | undefined;
  readonly onCollapse: () => void;
  readonly onToggle: (planId: string) => void;
};

/**
 * Section-scoped keyboard handler. Fires from the list wrapper (`role="toolbar"`
 * for "a group of controls some of which take keyboard navigation"), so the
 * listener's interactive role is satisfied. Only ACTS when focus is on a row
 * button (`activeIndex !== -1`); when focus is on the expanded breakdown's
 * "Open full view" link or its per-task copy button (`activeIndex === -1`),
 * the key passes through untouched rather than stealing focus back to a row.
 *
 * `/` (outside a typing target) focuses the search box.
 * `Escape` collapses the expanded row.
 * `ArrowDown`/`ArrowUp` cycle row focus with wrap-around.
 * `Enter` toggles the focused row.
 */
export function onCustomPlanListKeyDown(opts: CustomPlanListKeyDownOpts): void {
  const { event, listWrap, searchInput, onCollapse, onToggle } = opts;
  if (event.key === '/' && !isTypingTarget(event.target)) {
    event.preventDefault();
    searchInput?.focus();
    return;
  }
  if (event.key === 'Escape') {
    event.preventDefault();
    onCollapse();
    return;
  }
  const buttons = customPlanRowButtons(listWrap);
  if (buttons.length === 0) return;
  const activeIndex = buttons.indexOf(document.activeElement as HTMLButtonElement);
  // Focus is on something the listener sees (breakdown link/button, e.g.)
  // that is NOT a row — let the key through untouched rather than stealing
  // focus back to a row.
  if (activeIndex === -1) return;
  if (event.key === 'ArrowDown') {
    event.preventDefault();
    buttons[(activeIndex + 1) % buttons.length]?.focus();
  } else if (event.key === 'ArrowUp') {
    event.preventDefault();
    buttons[(activeIndex - 1 + buttons.length) % buttons.length]?.focus();
  } else if (event.key === 'Enter') {
    event.preventDefault();
    const planId = buttons[activeIndex]?.dataset['planId'];
    if (planId !== undefined) onToggle(planId);
  }
}
